import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Clock,
  AlertTriangle,
  User,
  Building2,
  Globe,
  CheckCircle2,
  RefreshCw,
  Plus,
  Search,
  MessageSquare,
  ChevronUp,
  ChevronDown,
  FileText,
  ShieldCheck,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProcessingTimePage = ({ user, settings }) => {
  const [trackingData, setTrackingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [extensionDays, setExtensionDays] = useState("");
  const [remarques, setRemarques] = useState("");
  const [raisonSouffrance, setRaisonSouffrance] = useState("");
  const [dateContact, setDateContact] = useState("");
  const [commentaireValidation, setCommentaireValidation] = useState("");
  const [validating, setValidating] = useState(false);
  const [sortField, setSortField] = useState("code_client");
  const [sortDirection, setSortDirection] = useState("asc");

  useEffect(() => {
    fetchTrackingData();
  }, []);

  const fetchTrackingData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/delais/tracking`, {
        withCredentials: true,
      });
      setTrackingData(response.data);
    } catch (error) {
      console.error("Error fetching tracking data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  // Filter incomplete files only
  const incompleteData = trackingData.filter((item) => !item.is_complete);

  // Filter based on search
  const filterBySearch = (data) => {
    if (!searchTerm) return data;
    const term = searchTerm.toLowerCase();
    return data.filter(
      (item) =>
        item.nom?.toLowerCase().includes(term) ||
        item.prenom?.toLowerCase().includes(term) ||
        item.code_client?.toLowerCase().includes(term)
    );
  };

  // Sort data
  const sortData = (data) => {
    return [...data].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle null values
      if (aVal === null || aVal === undefined) aVal = "";
      if (bVal === null || bVal === undefined) bVal = "";
      
      // String comparison
      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 inline ml-1" />
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("fr-CA");
    } catch {
      return dateStr;
    }
  };

  const getDelaiDisplay = (item) => {
    if (item.type_traitement === "En ligne") {
      if (item.jours_restants !== null && item.jours_restants !== undefined) {
        if (item.jours_restants <= 0) {
          return { text: "Dépassé", class: "bg-red-100 text-red-800 animate-pulse" };
        } else if (item.is_urgent) {
          return { text: `${item.jours_restants} jours`, class: "bg-orange-100 text-orange-800 animate-pulse" };
        } else {
          return { text: `${item.jours_restants} jours`, class: "bg-green-100 text-green-800" };
        }
      }
      return { text: "-", class: "bg-gray-100 text-gray-600" };
    } else {
      // Présentiel - show waiting time since date_debut_traitement
      if (item.date_debut_traitement) {
        const start = new Date(item.date_debut_traitement);
        const now = new Date();
        const diffDays = Math.floor((now - start) / (1000 * 60 * 60 * 24));
        
        // If date is in the future, show as not started yet
        if (diffDays < 0) {
          return { text: "À venir", class: "bg-blue-100 text-blue-800" };
        } else if (diffDays > 14) {
          return { text: `${diffDays} jours`, class: "bg-orange-100 text-orange-800" };
        } else if (diffDays > 7) {
          return { text: `${diffDays} jours`, class: "bg-yellow-100 text-yellow-800" };
        } else {
          return { text: `${diffDays} jours`, class: "bg-green-100 text-green-800" };
        }
      }
      return { text: "-", class: "bg-gray-100 text-gray-600" };
    }
  };

  const getBlockingReason = (item) => {
    if (item.raison_souffrance) return item.raison_souffrance;
    
    const reasons = [];
    if (!item.payment_complete) reasons.push("Paiement");
    if (!item.principal_signed) reasons.push("Signature");
    if (!item.principal_transmitted && item.payment_complete && item.principal_signed) {
      reasons.push("Transmission");
    }
    return reasons.length > 0 ? reasons.join(", ") : "En attente";
  };

  const handleRowClick = (client) => {
    setSelectedClient(client);
    setRaisonSouffrance(client.raison_souffrance || "");
    setDateContact(client.date_contact_document || "");
    setRemarques(client.remarques || "");
    setShowDetailModal(true);
  };

  const handleOpenRemarksModal = (client, e) => {
    e.stopPropagation();
    setSelectedClient(client);
    setRemarques(client.remarques || "");
    setShowRemarksModal(true);
  };

  const handleOpenExtendModal = (client, e) => {
    e.stopPropagation();
    setSelectedClient(client);
    setExtensionDays("");
    setShowExtendModal(true);
  };

  const handleSaveRemarks = async () => {
    if (!selectedClient) return;
    
    try {
      await axios.put(
        `${API}/delais/tracking/${selectedClient.client_id}`,
        { remarques },
        { withCredentials: true }
      );
      
      toast.success("Remarques enregistrées");
      setShowRemarksModal(false);
      fetchTrackingData();
    } catch (error) {
      console.error("Error saving remarks:", error);
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleSaveDetails = async () => {
    if (!selectedClient) return;
    
    try {
      await axios.put(
        `${API}/delais/tracking/${selectedClient.client_id}`,
        {
          raison_souffrance: raisonSouffrance,
          date_contact_document: dateContact,
          remarques,
        },
        { withCredentials: true }
      );
      
      toast.success("Informations mises à jour");
      setShowDetailModal(false);
      fetchTrackingData();
    } catch (error) {
      console.error("Error saving details:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleExtendDeadline = async () => {
    if (!selectedClient || !extensionDays) return;
    
    try {
      const currentDelai = selectedClient.delai_traitement || 0;
      const newDelai = currentDelai + parseInt(extensionDays);
      
      // Get full client data first
      const clientRes = await axios.get(`${API}/clients/${selectedClient.client_id}`, {
        withCredentials: true,
      });
      
      await axios.put(
        `${API}/clients/${selectedClient.client_id}`,
        {
          ...clientRes.data,
          delai_traitement: newDelai,
        },
        { withCredentials: true }
      );
      
      toast.success(`Délai prolongé de ${extensionDays} jours`);
      setShowExtendModal(false);
      fetchTrackingData();
    } catch (error) {
      console.error("Error extending deadline:", error);
      toast.error("Erreur lors de la prolongation du délai");
    }
  };

  const handleOpenValidateModal = () => {
    setCommentaireValidation("");
    setShowValidateModal(true);
  };

  const handleValidateEnRegle = async () => {
    if (!selectedClient) return;
    
    setValidating(true);
    try {
      await axios.post(
        `${API}/delais/validate/${selectedClient.client_id}`,
        { commentaire: commentaireValidation },
        { withCredentials: true }
      );
      
      toast.success("Dossier validé comme en règle");
      setShowValidateModal(false);
      setShowDetailModal(false);
      fetchTrackingData();
    } catch (error) {
      console.error("Error validating client:", error);
      toast.error("Erreur lors de la validation");
    } finally {
      setValidating(false);
    }
  };

  const filteredAndSortedData = sortData(filterBySearch(incompleteData));

  if (loading) {
    return (
      <Layout user={user} settings={settings}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} settings={settings}>
      <div className="space-y-6" data-testid="processing-time-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Suivi des délais
            </h1>
            <p className="text-muted-foreground mt-1">
              Liste complète des dossiers en cours de traitement ({incompleteData.length} dossiers)
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchTrackingData}
            data-testid="refresh-button"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par nom ou code client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>

        {/* Main Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Dossiers en cours
            </CardTitle>
            <CardDescription>
              Cliquez sur une ligne pour voir/modifier les détails
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredAndSortedData.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <p>Aucun dossier en attente de traitement</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="table-header-blue">
                      <TableHead 
                        className="cursor-pointer hover:bg-blue-800"
                        onClick={() => handleSort("code_client")}
                      >
                        Code <SortIcon field="code_client" />
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-blue-800"
                        onClick={() => handleSort("nom")}
                      >
                        Client <SortIcon field="nom" />
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-blue-800"
                        onClick={() => handleSort("type_traitement")}
                      >
                        Type <SortIcon field="type_traitement" />
                      </TableHead>
                      <TableHead>Raison du blocage</TableHead>
                      <TableHead>Temps restant</TableHead>
                      <TableHead className="text-center">Remarques</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAndSortedData.map((item) => {
                      const delaiInfo = getDelaiDisplay(item);
                      return (
                        <TableRow 
                          key={item.client_id}
                          className="cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleRowClick(item)}
                          data-testid={`row-${item.client_id}`}
                        >
                          <TableCell className="font-mono font-medium">
                            {item.code_client}
                          </TableCell>
                          <TableCell className="font-medium">
                            {item.prenom} {item.nom}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={
                                item.type_traitement === "En ligne" 
                                  ? "border-blue-500 text-blue-700 bg-blue-50" 
                                  : "border-amber-500 text-amber-700 bg-amber-50"
                              }
                            >
                              {item.type_traitement === "En ligne" ? (
                                <Globe className="h-3 w-3 mr-1" />
                              ) : (
                                <Building2 className="h-3 w-3 mr-1" />
                              )}
                              {item.type_traitement}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-orange-600 font-medium">
                              {getBlockingReason(item)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge className={delaiInfo.class}>
                              {delaiInfo.text === "Dépassé" && (
                                <AlertTriangle className="h-3 w-3 mr-1" />
                              )}
                              {delaiInfo.text !== "Dépassé" && delaiInfo.text !== "-" && (
                                <Clock className="h-3 w-3 mr-1" />
                              )}
                              {delaiInfo.text}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleOpenRemarksModal(item, e)}
                              className={item.remarques ? "text-blue-600" : "text-gray-400"}
                              data-testid={`remarks-${item.client_id}`}
                            >
                              <MessageSquare 
                                className={`h-5 w-5 ${item.remarques ? "fill-blue-100" : ""}`} 
                              />
                              {item.remarques && (
                                <span className="ml-1 w-2 h-2 bg-blue-500 rounded-full"></span>
                              )}
                            </Button>
                          </TableCell>
                          <TableCell>
                            {item.type_traitement === "En ligne" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => handleOpenExtendModal(item, e)}
                                data-testid={`extend-${item.client_id}`}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Prolonger
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Badge className="bg-green-100 text-green-800">Vert</Badge>
            <span>Délai confortable</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-orange-100 text-orange-800">Orange</Badge>
            <span>≤ 3 jours restants</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-red-100 text-red-800">Rouge</Badge>
            <span>Délai dépassé</span>
          </div>
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-blue-600 fill-blue-100" />
            <span>Contient des remarques</span>
          </div>
        </div>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                {selectedClient?.prenom} {selectedClient?.nom}
              </DialogTitle>
              <DialogDescription>
                Code: {selectedClient?.code_client} | Type: {selectedClient?.type_traitement}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground">Paiement</Label>
                  <p className={selectedClient?.payment_complete ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                    {selectedClient?.payment_complete ? "Complet" : "En attente"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Signature</Label>
                  <p className={selectedClient?.principal_signed ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                    {selectedClient?.principal_signed ? "Signée" : "En attente"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Transmission</Label>
                  <p className={selectedClient?.principal_transmitted ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                    {selectedClient?.principal_transmitted ? "Effectuée" : "En attente"}
                  </p>
                </div>
                {selectedClient?.type_traitement === "En ligne" && (
                  <div>
                    <Label className="text-muted-foreground">Jours restants</Label>
                    <p className="font-medium">
                      {selectedClient?.jours_restants !== null 
                        ? `${selectedClient?.jours_restants} jours`
                        : "-"}
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="raison-souffrance">Raison du blocage</Label>
                <Textarea
                  id="raison-souffrance"
                  value={raisonSouffrance}
                  onChange={(e) => setRaisonSouffrance(e.target.value)}
                  placeholder="Ex: Document T4 manquant, Relevé bancaire non fourni..."
                  rows={2}
                  data-testid="input-raison-souffrance"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date-contact">Date du dernier contact</Label>
                <Input
                  id="date-contact"
                  type="date"
                  value={dateContact}
                  onChange={(e) => setDateContact(e.target.value)}
                  data-testid="input-date-contact"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="remarques">Remarques</Label>
                <Textarea
                  id="remarques"
                  value={remarques}
                  onChange={(e) => setRemarques(e.target.value)}
                  placeholder="Notes additionnelles sur ce dossier..."
                  rows={3}
                  data-testid="input-remarques"
                />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button 
                variant="outline" 
                className="border-green-300 text-green-700 hover:bg-green-50"
                onClick={handleOpenValidateModal}
                data-testid="validate-en-regle-button"
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                Valider comme en règle
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                  Annuler
                </Button>
                <Button onClick={handleSaveDetails} data-testid="save-details-button">
                  Enregistrer
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Remarks Modal */}
        <Dialog open={showRemarksModal} onOpenChange={setShowRemarksModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                Remarques
              </DialogTitle>
              <DialogDescription>
                {selectedClient?.prenom} {selectedClient?.nom} ({selectedClient?.code_client})
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Textarea
                value={remarques}
                onChange={(e) => setRemarques(e.target.value)}
                placeholder="Ajoutez vos remarques ici..."
                rows={5}
                data-testid="modal-remarques"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRemarksModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleSaveRemarks} data-testid="save-remarks-button">
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Extend Deadline Modal */}
        <Dialog open={showExtendModal} onOpenChange={setShowExtendModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Prolonger le délai</DialogTitle>
              <DialogDescription>
                Voulez-vous augmenter le délai pour{" "}
                {selectedClient?.prenom} {selectedClient?.nom} ?
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="extension-days">
                  Nombre de jours additionnels
                </Label>
                <Input
                  id="extension-days"
                  type="number"
                  min="1"
                  value={extensionDays}
                  onChange={(e) => setExtensionDays(e.target.value)}
                  placeholder="Ex: 5"
                  data-testid="input-extension-days"
                />
              </div>
              {selectedClient && (
                <div className="text-sm text-muted-foreground">
                  <p>Délai actuel: {selectedClient.delai_traitement} {selectedClient.unite_delai}</p>
                  {extensionDays && (
                    <p className="text-primary font-medium mt-1">
                      Nouveau délai: {(selectedClient.delai_traitement || 0) + parseInt(extensionDays)} {selectedClient.unite_delai}
                    </p>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExtendModal(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleExtendDeadline}
                disabled={!extensionDays}
                data-testid="confirm-extend-button"
              >
                Confirmer la prolongation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Validate En Règle Modal */}
        <Dialog open={showValidateModal} onOpenChange={setShowValidateModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-700">
                <ShieldCheck className="h-5 w-5" />
                Valider le dossier comme en règle
              </DialogTitle>
              <DialogDescription>
                Confirmez que le dossier de{" "}
                <strong>{selectedClient?.prenom} {selectedClient?.nom}</strong> est en règle et peut être archivé.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Information:</strong> Ce dossier sera retiré de la liste de suivi active 
                  et déplacé vers l'historique. Vous pourrez le réactiver ultérieurement si nécessaire.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="commentaire-validation">Commentaire de validation *</Label>
                <Textarea
                  id="commentaire-validation"
                  value={commentaireValidation}
                  onChange={(e) => setCommentaireValidation(e.target.value)}
                  placeholder="Ex: Dossier complet, transmission effectuée, client satisfait..."
                  rows={3}
                  data-testid="input-commentaire-validation"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowValidateModal(false)} disabled={validating}>
                Annuler
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={handleValidateEnRegle}
                disabled={validating || !commentaireValidation.trim()}
                data-testid="confirm-validate-button"
              >
                {validating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                    Validation...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Confirmer la validation
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ProcessingTimePage;
